import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
} from "react";
import "../style/customValues.css";
import TemplateModal from "./treatmentTemplate";
import parse from "html-react-parser";
import { UserCircle, Trash2 } from "lucide-react";
import ModuleSection from "./modulesComponent";
import { useAuth } from "../context/authContext";
import axios from "axios";
import config from '../config';

const CustomValues = forwardRef((props, ref) => {
  const { authData } = useAuth();
  const { onLogoChange, campaignData, onChange, accountModules, canAccessInventory } = props;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [imageUrl, setImageUrl] = useState("");


  const [expandedModules, setExpandedModules] = useState([
    "Primary Care Module",
  ]);
  const [initialValues, setInitialValues] = useState(null);

  const [customValues, setCustomValues] = useState({
    globalQueue: "",
    usingGlobalQueue: "",
    communities: "",
    clinicPrograms: "",
    communitiesRestriction: false,
    clinicProgramsRestriction: false,
    globalLovRestriction: false,
    examTemplateData: [],
    printHealthCenterName: "",
    printAddress1: "",
    printAddress2: "",
    printPhone: "",
    organizationLogoFileName: "",

    primaryCareQueues: "",
    primaryCareDiagnosis: "",
    primaryCareTreatments: "",
    primaryCareDrugs: "",
    primaryCareDiagnosisRestriction: false,
    primaryCareTreatmentsRestriction: false,
    primaryCareDrugsRestriction: false,

    dentalVisitDrugs: "",
    dentalVisitDrugsRestriction: false,

    orthopedicQueues: "",
    orthopedicDiagnosis: "",
    orthopedicTreatments: "",
    orthopedicDrugs: "",
    orthopedicDiagnosisRestriction: false,
    orthopedicTreatmentsRestriction: false,
    orthopedicDrugsRestriction: false,

    pediatricQueues: "",
    pediatricDiagnoses: "",
    pediatricTreatments: "",
    pediatricDrugs: "",
    pediatricDiagnosesRestriction: false,
    pediatricTreatmentsRestriction: false,
    pediatricDrugsRestriction: false,

    generalSurgeryQueues: "",
    generalSurgeryDiagnoses: "",
    generalSurgeryTreatments: "",
    generalSurgeryDrugs: "",
    generalSurgeryDiagnosesRestriction: false,
    generalSurgeryTreatmentsRestriction: false,
    generalSurgeryDrugsRestriction: false,

    inPatientQueues: "",
    inPatientNotesDiagnoses: "",
    inPatientTreatmentTypes: "",
    inPatientDrugs: "",
    inPatientNotesDiagnosesRestriction: false,
    inPatientTreatmentTypesRestriction: false,
    inPatientDrugsRestriction: false,

    cleftLipQueues: "",
    cleftLipDiagnoses: "",
    cleftLipTreatments: "",
    cleftLipDrugs: "",
    cleftLipDiagnosesRestriction: false,
    cleftLipTreatmentsRestriction: false,
    cleftLipDrugsRestriction: false,

    laborDeliveryQueues: "",
    laborDeliveryDiagnoses: "",
    laborDeliveryTreatments: "",
    laborDeliveryDrugs: "",
    laborDeliveryDiagnosesRestriction: false,
    laborDeliveryTreatmentsRestriction: false,
    laborDeliveryDrugsRestriction: false,

    physiotherapyQueues: "",
    physiotherapyDiagnoses: "",
    physiotherapyTreatments: "",
    physiotherapyDrugs: "",
    physiotherapyDiagnosesRestriction: false,
    physiotherapyTreatmentsRestriction: false,
    physiotherapyDrugsRestriction: false,

    treatmentTemplateData: [],
    planTemplateData: [],

    // Module-specific templates (prevents Wound Care / Physiotherapy sharing).
    physiotherapyTreatmentTemplateData: [],
    woundCareTreatmentTemplateData: [],
    physiotherapyPlanTemplateData: [],

    antenatalQueues: "",
    antenatalDiagnoses: "",
    antenatalTreatments: "",
    antenatalDrugs: "",
    antenatalDiagnosesRestriction: false,
    antenatalTreatmentsRestriction: false,
    antenatalDrugsRestriction: false,

    woundCareQueues: "",
    woundCareDiagnoses: "",
    woundCareTreatments: "",
    woundCareDrugs: "",
    woundCareDiagnosesRestriction: false,
    woundCareTreatmentsRestriction: false,
    woundCareDrugsRestriction: false,
  });

  // Initialize customValues when in edit mode
  useEffect(() => {
    if (campaignData?.customValues) {
      let parsedCustomValues;

      try {
        parsedCustomValues =
          typeof campaignData.customValues === "string"
            ? JSON.parse(campaignData.customValues)
            : campaignData.customValues;

        // Ensure all required properties exist
        const completeValues = {
          ...customValues, // Default structure
          ...parsedCustomValues, // Loaded values
        };

        if (!completeValues.examTemplateData) {
          completeValues.examTemplateData = [];
        }

        setCustomValues(completeValues);
        setInitialValues(completeValues);
        console.log("Loaded custom values:", completeValues);
      } catch (error) {
        console.error("Error parsing custom values:", error);
      }
    }
  }, [campaignData]);

  useEffect(() => {
    if (onChange && initialValues) {
      const hasChanges =
        JSON.stringify(customValues) !== JSON.stringify(initialValues);
      if (hasChanges) {
        onChange(true);
      }
    }
  }, [customValues, onChange, initialValues]);

  useImperativeHandle(
    ref,
    () => ({
      getModuleData: () => customValues,
      setModuleData: (data) => {
        if (data && typeof data === "object") {
          setCustomValues(data);
          setInitialValues(data);
        }
      },
      hasChanges: () => {
        return initialValues
          ? JSON.stringify(customValues) !== JSON.stringify(initialValues)
          : false;
      },
    }),
    [customValues, initialValues]
  );

  useEffect(() => {
    if (customValues.organizationLogoFileName) {
      const fetchImageUrl = async () => {
        try {
          const response = await axios.get(
            `${config.BASE_URL}/get-image-url`,
            {
              params: {
                fileName: `${process.env.REACT_APP_S3_BUCKET_FOLDER}/${customValues.organizationLogoFileName}`,
              },
              headers: {
                Authorization: `Bearer ${authData.accessToken}`,
              },
            }
          );
          setImageUrl(response.data.presignedUrl);
        } catch (error) {
          console.error("Error fetching image URL:", error);
        }
      };
      fetchImageUrl();
    }
  }, [customValues.organizationLogoFileName, authData.accessToken]);

  const handleModuleChange = (key, value) => {
    setCustomValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const templateKeyFor = (moduleKey, templateType) => {
    if (!moduleKey) return `${templateType}TemplateData`;
    if (templateType === "treatment") return `${moduleKey}TreatmentTemplateData`;
    if (templateType === "plan") return `${moduleKey}PlanTemplateData`;
    return `${templateType}TemplateData`;
  };

  const handleSaveTemplate = (templateType, templateName, templateDetails) => {
    setCustomValues((prev) => {
      const existingTemplates = prev[`${templateType}TemplateData`] || [];
      const updatedTemplates = selectedTemplate
        ? existingTemplates.map((t) =>
          t.templateName === selectedTemplate.templateName
            ? { templateName, templateDetails }
            : t
        )
        : [...existingTemplates, { templateName, templateDetails }];

      return {
        ...prev,
        [`${templateType}TemplateData`]: updatedTemplates,
      };
    });
    setSelectedTemplate(null);
    setIsModalOpen(false);
  };

  const handleDeleteTemplate = (templateType, index) => {
    setCustomValues((prev) => ({
      ...prev,
      [`${templateType}TemplateData`]: (
        prev[`${templateType}TemplateData`] || []
      ).filter((_, i) => i !== index),
    }));
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
      alert("Please upload a valid image file (png, jpg, jpeg).");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      alert("File size should not exceed 2MB.");
      return;
    }

    setUploadingLogo(true);

    try {
      const response = await axios.get(
        `${config.BASE_URL}/get-presigned-url`,
        {
          params: {
            fileName: file.name,
            contentType: file.type,
          },
          headers: {
            Authorization: `Bearer ${authData.accessToken}`,
          },
        }
      );

      const { presignedUrl } = response.data;
      const uploadResponse = await fetch(presignedUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(
          `Failed to upload the file to S3: ${uploadResponse.statusText}`
        );
      }
      const newFileName = file.name;

      setCustomValues((prev) => ({
        ...prev,
        organizationLogoFileName: newFileName,
      }));
      setImageUrl(""); // immediately clear old image to prevent flash

      // Immediately fetch new presigned URL after upload
      try {
        const imageResponse = await axios.get(
          `${config.BASE_URL}/get-image-url`,
          {
            params: {
              fileName: `${process.env.REACT_APP_S3_BUCKET_FOLDER}/${newFileName}`,
            },
            headers: {
              Authorization: `Bearer ${authData.accessToken}`,
            },
          }
        );
        setImageUrl(imageResponse.data.presignedUrl);
      } catch (error) {
        console.error("Error refreshing image URL:", error);
      }

      if (onLogoChange) {
        onLogoChange(newFileName);
      }

    } catch (error) {
      console.error("Logo upload error:", error);
      alert("Failed to upload the logo. Please try again.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoDelete = () => {
    const confirmed = window.confirm("Are you sure you want to delete the logo?");
    if (!confirmed) return;

    setCustomValues((prev) => ({
      ...prev,
      organizationLogoFileName: "",
    }));
    setImageUrl("");
  };


  const toggleModule = (moduleName) => {
    setExpandedModules((prev) =>
      prev.includes(moduleName)
        ? prev.filter((name) => name !== moduleName)
        : [...prev, moduleName]
    );
  };

  const renderFormattedText = (html) => {
    return parse(html);
  };
  const allModuleNames = [
    "Primary Care Module",
    "Pediatric Module",
    "Antenatal Module",
    "Cleft Lip & Palate Module",
    "General Surgery Module",
    "In Patient Module",
    "Labor & Delivery Module",
    "Orthopedic Module",
    "Physiotherapy Module",
    "Wound Care Module",
    "Dental Module",
  ];
  const displayKeyMap = {
    "Primary Care Module": "primaryCare",
    "Dental Module": "dentalVisit",
    "Pediatric Module": "pediatric",
    "Antenatal Module": "preNatal",
    "Cleft Lip & Palate Module": "cleftLip",
    "General Surgery Module": "generalSurgery",
    "In Patient Module": "inPatient",
    "Labor & Delivery Module": "laborDelivery",
    "Orthopedic Module": "orthopedic",
    "Physiotherapy Module": "physiotherapy",
    "Wound Care Module": "woundCare",
  };
  const filteredModules = allModuleNames.filter(
    (name) => accountModules.includes(displayKeyMap[name])
  );

  return (
    <div className="custom-values-container">
      <h2>
        <b>Custom Values</b>
      </h2>

      <div className="global-queue-section">
        <div className="global-queue-content">
          <div className="queue-section">
            <label>
              <input
                type="checkbox"
                checked={!!customValues.usingGlobalQueue}
                onChange={() =>
                  setCustomValues((prev) => ({
                    ...prev,
                    usingGlobalQueue: !prev.usingGlobalQueue,
                  }))
                }
              />
              <span style={{ marginLeft: "8px" }}>Global Queue</span>
            </label>
            <textarea
              value={customValues.globalQueue || ""}
              onChange={(e) =>
                setCustomValues((prev) => ({
                  ...prev,
                  globalQueue: e.target.value,
                }))
              }
            />
            <p className="info-text">
              Select a Global Queue List if you'd like your entire clinic to use the
              same clinic flow through clinic stations.
              <br />
              Do not enable a Global Queue List if each department (or module) will
              have a different clinic flow. Instead, define Module-Specific Queue
              Lists below.
            </p>

            {!!customValues.globalQueue && (
              <p style={{ color: "red", marginTop: "8px" }}>
                Warning: Any patients currently in the previous Queues will be
                removed if Global Queues enabled.
              </p>
            )}
          </div>

          <div className="lov-restriction-section">
            <label>
              <input
                type="checkbox"
                checked={!!customValues.globalLovRestriction}
                onChange={() =>
                  setCustomValues((prev) => ({
                    ...prev,
                    globalLovRestriction: !prev.globalLovRestriction,
                  }))
                }
              />
              <span style={{ marginLeft: "8px" }}>Users must select from drop down list ONLY</span>
            </label>
            <p className="info-text">
              Enable this feature if you want to restrict all users from typing a value that is not in the list into any field with drop down values. This includes static lists or custom lists (below).
            </p>
            <p className="info-text">
              If you want to restrict only certain custom list of values fields, do NOT enable this feature, and select the restriction setting next to the field you'd like to restrict to values in the list only (below).
            </p>
          </div>
        </div>
      </div>

      <div className="global-custom-lovs-section">

        <div className="form-group">
          <label>Communities</label>
          <div className="field-with-restriction">
            <textarea
              value={customValues.communities || ""}
              onChange={(e) =>
                setCustomValues((prev) => ({
                  ...prev,
                  communities: e.target.value,
                }))
              }
              placeholder="Enter list of communities..."
            />
            <div className="restriction-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={customValues.globalLovRestriction || customValues.communitiesRestriction}
                  onChange={(e) =>
                    setCustomValues((prev) => ({
                      ...prev,
                      communitiesRestriction: e.target.checked,
                    }))
                  }
                  disabled={customValues.globalLovRestriction}
                />
                <span style={{ marginLeft: "8px" }}>Users must select from drop down list ONLY</span>
              </label>
              <p className="restriction-info">
                Enable if you want to restrict users from typing in their own value that is not in the list of values within the drop down for this field.
              </p>
            </div>
          </div>
        </div>

        <div className="form-group">
          <label>Clinic Programs</label>
          <div className="field-with-restriction">
            <textarea
              value={customValues.clinicPrograms || ""}
              onChange={(e) =>
                setCustomValues((prev) => ({
                  ...prev,
                  clinicPrograms: e.target.value,
                }))
              }
              placeholder="Enter list of clinic programs..."
            />
            <div className="restriction-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={customValues.globalLovRestriction || customValues.clinicProgramsRestriction}
                  onChange={(e) =>
                    setCustomValues((prev) => ({
                      ...prev,
                      clinicProgramsRestriction: e.target.checked,
                    }))
                  }
                  disabled={customValues.globalLovRestriction}
                />
                <span style={{ marginLeft: "8px" }}>Users must select from drop down list ONLY</span>
              </label>
              <p className="restriction-info">
                Enable if you want to restrict users from typing in their own value that is not in the list of values within the drop down for this field.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="notes-template-container">
        <div className="global-notes-templates">
          <h3>Global Notes Templates</h3>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Template Name</th>
                <th>
                  <div className="th-content">
                    <span>Template Details</span>
                    <button
                      className="btn btn-primary add-template-btn"
                      onClick={() => setIsModalOpen(true)}
                    >
                      + Add Template
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {customValues.examTemplateData?.map((template, index) => (
                <tr key={index}>
                  <td>{template.templateName}</td>
                  <td className="table-cell">
                    <span className="truncated-text">
                      {renderFormattedText(template.templateDetails)}
                    </span>
                    <div className="dropdown">
                      <button
                        className="dropdown-toggle"
                        type="button"
                        onClick={() =>
                          setOpenDropdown(openDropdown === index ? null : index)
                        }
                      >
                        &#9662;
                      </button>
                      {openDropdown === index && (
                        <div className="dropdown-menu show">
                          <button
                            className="dropdown-item"
                            onClick={() => {
                              setOpenDropdown(null);
                              setSelectedTemplate(template);
                              setIsModalOpen(true);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="dropdown-item"
                            onClick={() => {
                              setOpenDropdown(null);
                              if (
                                window.confirm(
                                  "Are you sure you want to delete this entry?"
                                )
                              ) {
                                handleDeleteTemplate("exam", index);
                              }
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p
            className="info-text"
            style={{ marginTop: "20px", marginBottom: "30px" }}
          >
            Exam/Surgical Note Templates will auto-populate the Exam or Surgical
            Note across any module based on the Template selected.
          </p>
        </div>
      </div>

      <div className="visit-summary-container">
        <h2>
          <b>Visit Summary Print Values</b>
        </h2>
        <div className="visit-summary-content">
          <div className="visit-summary-form">
            <div className="form-group">
              <label htmlFor="healthCenterName">Health Center Name</label>
              <input
                id="healthCenterName"
                value={customValues.printHealthCenterName || ""}
                onChange={(e) =>
                  setCustomValues((prev) => ({
                    ...prev,
                    printHealthCenterName: e.target.value,
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label htmlFor="address1">Address 1</label>
              <input
                id="address1"
                value={customValues.printAddress1 || ""}
                onChange={(e) =>
                  setCustomValues((prev) => ({
                    ...prev,
                    printAddress1: e.target.value,
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label htmlFor="address2">Address 2</label>
              <input
                id="address2"
                value={customValues.printAddress2 || ""}
                onChange={(e) =>
                  setCustomValues((prev) => ({
                    ...prev,
                    printAddress2: e.target.value,
                  }))
                }
              />
            </div>
            <div className="form-group">
              <label htmlFor="phone">Phone #</label>
              <input
                id="phone"
                value={customValues.printPhone || ""}
                onChange={(e) =>
                  setCustomValues((prev) => ({
                    ...prev,
                    printPhone: e.target.value,
                  }))
                }
              />
            </div>
          </div>
          <div className="organization-logo-section">
            <label>Organization Logo</label>
            <div className="logo-upload-area">
              {customValues.organizationLogoFileName ? (
                <>
                  <button
                    className="delete-logo-btn"
                    onClick={handleLogoDelete}
                  >
                    <Trash2 size={20} />
                  </button>
                  <label htmlFor="logo-upload">
                    <img
                      src={imageUrl}
                      alt="Organization Logo"
                      className="uploaded-logo"
                    />
                  </label>
                </>
              ) : (
                <label htmlFor="logo-upload">
                  <UserCircle size={100} />
                </label>
              )}
              <input
                type="file"
                id="logo-upload"
                accept=".png,.jpg,.jpeg"
                onChange={(e) => {
                  handleLogoUpload(e);
                  e.target.value = "";
                }}
                style={{ display: "none" }}
                disabled={uploadingLogo}
              />
            </div>

            <p className="file-info">
              Accepted file types: .png, .jpg, .jpeg <br />Maximum file
              size: 2 MB
            </p>
          </div>
        </div>
      </div>

      <div className="modules-container">
        {filteredModules.map((moduleName) => (
          <div key={moduleName} className="expandable-module">
            {/* exactly the same rendering logic as before */}
            <div
              className={`module-header ${expandedModules.includes(moduleName) ? "active" : ""
                }`}
              onClick={() => toggleModule(moduleName)}
            >
              <span>{moduleName}</span>
              <span>
                {expandedModules.includes(moduleName) ? "▲" : "▼"}
              </span>
            </div>
            {expandedModules.includes(moduleName) && (
              <div className="module-content">
                <ModuleSection
                  moduleName={moduleName}
                  customValues={customValues}
                  onChange={handleModuleChange}
                  onTemplatesChange={(moduleKey, templateType, updatedTemplates) =>
                    setCustomValues((prev) => ({
                      ...prev,
                      [templateKeyFor(moduleKey, templateType)]: updatedTemplates,
                    }))
                  }
                  usingGlobalQueue={customValues.usingGlobalQueue}
                  hideMedication={canAccessInventory}
                  globalLovRestriction={customValues.globalLovRestriction}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {isModalOpen && (
        <TemplateModal
          onSave={handleSaveTemplate}
          onClose={() => setIsModalOpen(false)}
          selectedTemplate={selectedTemplate}
          templateType="exam"
        />
      )}
    </div>
  );
});

export default CustomValues;
